import { LinearProgress, Stack, Typography } from '@mui/material';
import { FC, useEffect, useState } from 'react';
import AlertMUI from '../../../../components/AlertMui/AlertMui';
import { AllocationState } from './useWeightedScenarios';

export const AllocationCopy: Record<AllocationState, string> = {
  under:
    'Chance values must sum to exactly 100% to enable Weighted Outcome. Increase total allocation to continue.',
  over: 'Chance values exceed 100%. Reduce the total allocation to enable Weighted Outcome.',
  complete: 'Chance values total 100%. Weighted Outcome is now available.',
};

const SeverityByState: Record<AllocationState, 'warning' | 'error' | 'success'> = {
  under: 'warning',
  over: 'error',
  complete: 'success',
};

const SUCCESS_AUTO_DISMISS_MS = 1000;

interface IAllocationBannerProps {
  allocationTotal: number;
  allocationState: AllocationState;
}

export const AllocationBanner: FC<IAllocationBannerProps> = ({ allocationTotal, allocationState }) => {
  // The success banner auto-dismisses 1s after the allocation first reaches 100%.
  const [showSuccess, setShowSuccess] = useState(false);
  useEffect(() => {
    if (allocationState !== 'complete') {
      setShowSuccess(false);
      return;
    }
    setShowSuccess(true);
    const timer = setTimeout(() => setShowSuccess(false), SUCCESS_AUTO_DISMISS_MS);
    return () => clearTimeout(timer);
  }, [allocationState]);

  const bannerVisible = allocationState !== 'complete' || showSuccess;
  if (!bannerVisible) {
    return null;
  }

  // The whole strip is the colored banner: the note is the content, the running total + bar
  // are the Alert's right-aligned `action`, so they sit on top of the banner background.
  return (
    <AlertMUI
      severity={SeverityByState[allocationState]}
      sx={{ marginBottom: '1rem', '& .MuiAlert-action': { alignItems: 'center', pt: 0, mr: 0 } }}
      action={
        <Stack direction='row' alignItems='center' gap='0.75rem'>
          <Typography variant='body2' color='text.secondary' aria-label='allocation total' noWrap>
            {`${+allocationTotal.toFixed(2)}% of 100%`}
          </Typography>
          <LinearProgress
            variant='determinate'
            value={Math.min(allocationTotal, 100)}
            color={
              allocationState === 'over' ? 'error' : allocationState === 'complete' ? 'success' : 'warning'
            }
            sx={{ width: '12rem', borderRadius: 1, height: 6 }}
          />
        </Stack>
      }
    >
      {AllocationCopy[allocationState]}
    </AlertMUI>
  );
};
